import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faRedo, faTrashCan, faSearch } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import testimonialApi from '../../api/testimonialApi';
import { useToast } from '../../contexts/ToastContext';

const TrashedTestimonials = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [testimonialToActOnId, setTestimonialToActOnId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchTrashedTestimonials = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await testimonialApi.getTestimonials({ page, perPage, search, includeTrashed: true });
      setTestimonials(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch trashed testimonials:', err);
      setError('Failed to load trashed testimonials. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrashedTestimonials(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchTrashedTestimonials, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleRestoreClick = (id) => {
    setTestimonialToActOnId(id);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    setIsRestoreModalOpen(false);
    if (testimonialToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await testimonialApi.restoreTestimonial(testimonialToActOnId);
        addToast('Testimonial restored successfully!', 'success');
        await fetchTrashedTestimonials(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to restore testimonial:', err);
        addToast(err.response?.data?.message || 'Failed to restore testimonial. Please try again.', 'error');
        setError('Failed to restore testimonial. Please try again.');
      } finally {
        setLoading(false);
        setTestimonialToActOnId(null);
      }
    }
  };

  const handlePermanentDeleteClick = (id) => {
    setTestimonialToActOnId(id);
    setIsPermanentDeleteModalOpen(true);
  };

  const handleConfirmPermanentDelete = async () => {
    setIsPermanentDeleteModalOpen(false);
    if (testimonialToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await testimonialApi.permanentDeleteTestimonial(testimonialToActOnId);
        addToast('Testimonial permanently deleted successfully!', 'success');
        await fetchTrashedTestimonials(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to permanently delete testimonial:', err);
        addToast(err.response?.data?.message || 'Failed to permanently delete testimonial. Please try again.', 'error');
        setError('Failed to permanently delete testimonial. Please try again.');
      } finally {
        setLoading(false);
        setTestimonialToActOnId(null);
      }
    }
  };

  const handleCancelAction = () => {
    setIsRestoreModalOpen(false);
    setIsPermanentDeleteModalOpen(false);
    setTestimonialToActOnId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white">Trashed Testimonials</h1>
        <Button
          onClick={() => navigate('/testimonials')}
          className="bg-accent-red hover:bg-red-700 text-white"
        >
          Back to Testimonials
        </Button>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Trashed Testimonials"
          id="search-trashed-testimonials"
          name="search-trashed-testimonials"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by client name, designation or content..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Client Name</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Designation</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {testimonials.length > 0 ? (
                  testimonials.map((testimonial) => (
                    <tr key={testimonial.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{testimonial.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{testimonial.name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{testimonial.client_designation}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleRestoreClick(testimonial.id)}
                          className="text-green-500 hover:text-green-600 mr-3"
                          icon={faRedo}
                          tooltipId={`restore-testimonial-tooltip-${testimonial.id}`}
                          tooltipContent="Restore"
                        />
                        <Button
                          onClick={() => handlePermanentDeleteClick(testimonial.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrashCan}
                          tooltipId={`permanent-delete-testimonial-tooltip-${testimonial.id}`}
                          tooltipContent="Permanent Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-3 px-4 text-center text-sm text-text-gray">No trashed testimonials found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmRestore}
        title="Confirm Restore"
        message="Are you sure you want to restore this testimonial?"
      />

      <ConfirmationModal
        isOpen={isPermanentDeleteModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmPermanentDelete}
        title="Confirm Permanent Delete"
        message="Are you sure you want to permanently delete this testimonial? This action cannot be undone."
      />
    </div>
  );
};

export default TrashedTestimonials;